import Layout from '../components/Layout'
import { Badge } from '../components/ui/badge'
import { BracketPickCell, BracketActualWinnerCell, EarnedPoints, PendingPoints } from '../components/bracket'

// ─── Mock Data ───────────────────────────────────────────────────────────────

const T = {
  t1: { id: 't1', seed: 1, players: [{ name: 'Anna Bright' }, { name: 'Anna Leigh Waters' }] },
  t2: { id: 't2', seed: 4, players: [{ name: 'Rachel Rohrabacher' }, { name: 'Christian Alshon' }] },
  t3: { id: 't3', seed: 8, players: [{ name: 'Valerie Simon' }, { name: 'Victoria Simon' }] },
  t4: { id: 't4', seed: 12, players: [{ name: 'Jade Kawamoto' }, { name: 'Vivienne David' }] },
  s1: { id: 's1', seed: 1, players: [{ name: 'Ben Johns' }] },
  s2: { id: 's2', seed: 5, players: [{ name: 'Tyson McGuffin' }] },
  tbd: { id: 'tbd', seed: null, players: [] },
}

// ─── Card Wrapper ────────────────────────────────────────────────────────────

function MockCard({ label, description, roundLabel = 'R1-1', points, cardState = '', children, teamSize = 2 }) {
  const cellCount = Array.isArray(children) ? children.length : 1
  const cellH = teamSize > 1 ? 64 : 40
  const minHeight = 38 + cellCount * cellH

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h4 className="text-sm font-semibold text-[var(--mauve-12)]">{label}</h4>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div
        className={`bracket-match-card bracket-match-card-glass bracket-match-card--hoverable border border-[var(--color-bracket-card-border)] rounded-lg relative w-full ${cardState} flex flex-col overflow-hidden`}
        style={{ '--bracket-card-min-height': `${minHeight}px` }}
      >
        <div className="px-3 py-1.5 border-b border-[var(--color-bracket-card-border)] bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Badge variant="secondary" className="bracket-round-badge text-xs">
              {roundLabel}
            </Badge>
            {points}
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function DevCardStates() {
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Bracket Card States</h1>
          <p className="text-muted-foreground mt-1">Every permutation of the bracket match card, for visual testing.</p>
        </div>

        {/* ── Core Pick States (Doubles) ─────────────────────────── */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-4 border-b border-[var(--color-bracket-card-border)] pb-2">Core Pick States (Doubles)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 1. Unpicked */}
            <MockCard label="1. Unpicked" description="No pick made yet. Both cells hoverable.">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} canEditPicks showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            {/* 2. Predicted (pending) */}
            <MockCard label="2. Predicted (Pending)" description="User picked team 1. No result yet." points={<PendingPoints value={1} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            {/* 3. Winner (correct pick, not champion) */}
            <MockCard label="3. Winner" description="User picked team 1 and they won. White bg + green accent bar." cardState="bracket-match-card--correct" points={<EarnedPoints value={1} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner hasOfficialWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            {/* 4. Wrong pick */}
            <MockCard label="4. Wrong Pick" description="User picked team 1 but team 2 won." cardState="bracket-match-card--wrong">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickIncorrect hasOfficialWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            {/* 5. Champion predicted */}
            <MockCard label="5. Champion Predicted" description="User's champion pick, still pending." roundLabel="R5-1" points={<PendingPoints value={16} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected championTeamId={T.t1.id} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            {/* 6. Champion correct */}
            <MockCard label="6. Champion Correct" description="User's champion pick won the tournament. Neon gradient, card glow." roundLabel="R5-1" cardState="bracket-match-card--correct bracket-match-card--champion" points={<EarnedPoints value={16} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner isChampionCorrect hasOfficialWinner championTeamId={T.t1.id} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

          </div>
        </section>

        {/* ── Points Display Variants ────────────────────────────── */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-4 border-b border-[var(--color-bracket-card-border)] pb-2">Points Display Variants</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 7. Pending points (+1) */}
            <MockCard label="7. Pending Points (+1)" description="Round 1 — low stakes." roundLabel="R1-3" points={<PendingPoints value={1} />}>
              <BracketPickCell team={T.t3} teamId={T.t3.id} teamSize={2} isSelected showBorderB />
              <BracketPickCell team={T.t4} teamId={T.t4.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            {/* 8. Earned points (+16) */}
            <MockCard label="8. Earned Points (+16)" description="Round 5 — high stakes, correct (non-champion winner)." roundLabel="R5-1" cardState="bracket-match-card--correct" points={<EarnedPoints value={16} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner hasOfficialWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

          </div>
        </section>

        {/* ── Edge Cases (3-4 Cell Cards) ─────────────────────────── */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-4 border-b border-[var(--color-bracket-card-border)] pb-2">Edge Cases (3-4 Cell Cards)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 9. Wrong pick with actual winner shown (3 cells) */}
            <MockCard label="9. Wrong Pick + Actual Winner" description="User picked team 1 (wrong). Actual contender (team 3) shown." cardState="bracket-match-card--wrong">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickIncorrect hasOfficialWinner showBorderB={false} />
              <BracketActualWinnerCell team={T.t3} teamSize={2} isWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            {/* 10. Both picks wrong (4 cells) */}
            <MockCard label="10. Both Picks Wrong" description="Both user picks were wrong. Actual contenders shown." cardState="bracket-match-card--wrong">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickIncorrect hasOfficialWinner showBorderB={false} />
              <BracketActualWinnerCell team={T.t3} teamSize={2} showBorderB />
              <BracketActualWinnerCell team={T.t4} teamSize={2} showBorderB={false} />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} isSelected isPickIncorrect hasOfficialWinner showBorderB={false} />
            </MockCard>

          </div>
        </section>

        {/* ── Additional States ───────────────────────────────────── */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-4 border-b border-[var(--color-bracket-card-border)] pb-2">Additional States</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 11. TBD team */}
            <MockCard label="11. TBD Team" description="One slot has a team, the other is TBD.">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} canEditPicks showBorderB />
              <BracketPickCell team={T.tbd} teamSize={2} showBorderB={false} />
            </MockCard>

            {/* 12. Eliminated from previous round */}
            <MockCard label="12. Eliminated (Previous Round)" description="Team 1 was picked in a prior round but lost. Shown with red strikethrough.">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} eliminatedEarlier showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} isSelected showBorderB={false} />
            </MockCard>

            {/* 13. Singles format */}
            <MockCard label="13. Singles Format" description="One player per cell instead of two." teamSize={1} points={<PendingPoints value={2} />} roundLabel="R2-1">
              <BracketPickCell team={T.s1} teamId={T.s1.id} teamSize={1} isSelected showBorderB />
              <BracketPickCell team={T.s2} teamId={T.s2.id} teamSize={1} canEditPicks showBorderB={false} />
            </MockCard>

          </div>
        </section>

        {/* ── All States Side by Side (Compact) ───────────────────── */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold mb-4 border-b border-[var(--color-bracket-card-border)] pb-2">Cell State Comparison</h2>
          <p className="text-xs text-muted-foreground mb-4">Same teams, different cell states — for comparing accent bars, backgrounds, and icons.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

            <MockCard label="Unpicked" roundLabel="R1-1">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} canEditPicks showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            <MockCard label="Predicted" roundLabel="R1-1" points={<PendingPoints value={1} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            <MockCard label="Winner" roundLabel="R1-1" cardState="bracket-match-card--correct" points={<EarnedPoints value={1} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner hasOfficialWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            <MockCard label="Wrong" roundLabel="R1-1" cardState="bracket-match-card--wrong">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickIncorrect hasOfficialWinner showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            <MockCard label="Champion (Predicted)" roundLabel="R5-1" points={<PendingPoints value={16} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected championTeamId={T.t1.id} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

            <MockCard label="Champion (Correct)" roundLabel="R5-1" cardState="bracket-match-card--correct bracket-match-card--champion" points={<EarnedPoints value={16} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner isChampionCorrect hasOfficialWinner championTeamId={T.t1.id} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            <MockCard label="Champion Winner (Earlier Round)" roundLabel="R2-1" cardState="bracket-match-card--correct" points={<EarnedPoints value={2} />}>
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} isSelected isPickCorrect isWinner isChampionCorrect hasOfficialWinner championTeamId={T.t1.id} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} showBorderB={false} />
            </MockCard>

            <MockCard label="Eliminated" roundLabel="R2-1">
              <BracketPickCell team={T.t1} teamId={T.t1.id} teamSize={2} eliminatedEarlier showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} isSelected showBorderB={false} />
            </MockCard>

            <MockCard label="TBD" roundLabel="R3-1">
              <BracketPickCell team={T.tbd} teamSize={2} showBorderB />
              <BracketPickCell team={T.t2} teamId={T.t2.id} teamSize={2} canEditPicks showBorderB={false} />
            </MockCard>

          </div>
        </section>

      </div>
    </Layout>
  )
}
